import {useMemo} from 'react';

import type {
  IUseLongTermCropsSubStepParams,
  IUseLongTermCropsSubStepResult,
} from '../productionInfo.types';

export function lshUseLongTermCropsSubStep({
  longTermCropOptions,
  longTermInitialRecords,
  longTermVarietyRecords,
}: IUseLongTermCropsSubStepParams): IUseLongTermCropsSubStepResult {
  const longTermCropNameById = useMemo(() => {
    const map = new Map<string, string>();

    longTermCropOptions.forEach((crop) => {
      map.set(crop.id, crop.name);
    });

    longTermInitialRecords.forEach((record) => {
      const cropId = record?.crop?.id;
      if (cropId === undefined || cropId === null) return;
      const key = String(cropId);
      if (!map.has(key)) {
        map.set(key, String(record?.crop?.name || '').trim() || 'Unknown crop');
      }
    });

    return map;
  }, [longTermCropOptions, longTermInitialRecords]);

  const longTermVarietyOptionsByCropId = useMemo(() => {
    const map = new Map<string, Array<{value: string; label: string}>>();
    const produceByName = new Map<
      string,
      (typeof longTermVarietyRecords)[number]
    >();

    longTermVarietyRecords.forEach((record) => {
      const name = String(record?.name || '').trim();
      if (!name) return;
      produceByName.set(name.toLowerCase(), record);
    });

    longTermCropNameById.forEach((cropName, cropId) => {
      const produce = produceByName.get(cropName.toLowerCase());
      const options =
        produce?.variety
          ?.map((item) => String(item?.name || '').trim())
          .filter((name) => Boolean(name))
          .filter((name, index, arr) => arr.indexOf(name) === index)
          .map((name) => ({value: name, label: name})) ?? [];

      if (options.length === 0 && cropName) {
        options.push({value: cropName, label: cropName});
      }

      map.set(cropId, options);
    });

    return map;
  }, [longTermCropNameById, longTermVarietyRecords]);

  const longTermAllVarietyOptions = useMemo(() => {
    const values = new Set<string>();

    longTermVarietyOptionsByCropId.forEach((options) => {
      options.forEach((option) => {
        const value = option.value.trim();
        if (!value) return;
        values.add(value);
      });
    });

    return Array.from(values).sort((left, right) =>
      left.toLowerCase().localeCompare(right.toLowerCase()),
    );
  }, [longTermVarietyOptionsByCropId]);

  const longTermVarietyLookupByCropId = useMemo(() => {
    const lookup = new Map<string, Map<string, string>>();

    longTermVarietyOptionsByCropId.forEach((options, cropId) => {
      const map = new Map<string, string>();
      options.forEach((option) => {
        const value = option.value.trim();
        if (!value) return;
        map.set(value.toLowerCase(), value);
      });
      lookup.set(cropId, map);
    });

    return lookup;
  }, [longTermVarietyOptionsByCropId]);

  return {
    longTermCropNameById,
    longTermVarietyOptionsByCropId,
    longTermAllVarietyOptions,
    longTermVarietyLookupByCropId,
  };
}
